import React, { useEffect, useState, useMemo } from "react";
import AlertSuccess from "../componets/AlerSuccess";
import { useWindowSize } from "../componets/useWindowSize";
import AppShell from "../componets/AppShell";

import {
  getButtonCollapsedMemory,
  getMenuCollapsedMemory,
  getVersionApp,
  setButtonCollapsedmemory,
  setMenuCollapsedmemory,
} from "../data/memory";

import { getDatabase, ref, onValue } from "firebase/database";
import { autoResetJogosMensal } from "../data/autoClear";
import { resetList } from "../data/resetList";
import { FaSpinner, FaRegSadTear, FaBirthdayCake } from "react-icons/fa";
import { saveAppVersion } from "../data/userService";

interface Aniversariante {
  displayName: string;
  aniversario: string; // formato YYYY-MM-DD
}

const Aniversario: React.FC = () => {
  const { width } = useWindowSize();
  // Ponto de quebra padrão (sm) do Tailwind para determinar o modo de visualização
  const isMobileView = width < 640;

  const [aniversariantes, setAniversariantes] = useState<Aniversariante[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isMenuCollapsed, setIsMenuCollapsed] = useState(
    getMenuCollapsedMemory()
  );
  const [isButtonCollapsed, setIsButtonCollapsed] = useState(
    getButtonCollapsedMemory()
  );
  const [successAlert, setSuccessAlert] = useState({
    open: false,
    message: "",
  });

  const today = new Date();
  const mesAtual = today.getMonth() + 1; // Mês atual (1 a 12)
  const diaAtual = today.getDate();
  const nomeMesAtual = today.toLocaleString("pt-BR", { month: "long" });

  // 1. Pré-processamento dos aniversariantes em um Mapa (para acesso rápido por dia)
  const aniversariantesPorDia = useMemo(() => {
    const map = new Map<number, Aniversariante[]>();

    aniversariantes
      .filter((entry) => {
        const [, month] = entry.aniversario.split("-");
        return Number(month) === mesAtual;
      })
      .forEach((entry) => {
        const [, , dayStr] = entry.aniversario.split("-");
        const day = Number(dayStr);

        if (!map.has(day)) {
          map.set(day, []);
        }
        map.get(day)?.push(entry);
      });

    return map;
  }, [aniversariantes, mesAtual]);

  // Lista de todos os aniversariantes do mês, ordenada por dia
  const aniversariantesDoMesOrdenados = useMemo(() => {
    return Array.from(aniversariantesPorDia.values())
      .flat()
      .sort((a, b) => {
        const diaA = Number(a.aniversario.split("-")[2]);
        const diaB = Number(b.aniversario.split("-")[2]);
        return diaA - diaB;
      });
  }, [aniversariantesPorDia]);

  useEffect(() => {
    resetList();
    saveAppVersion();
    autoResetJogosMensal();
  }, []);

  useEffect(() => {
    if (width < 1000) {
      setIsMenuCollapsed(true);
      setMenuCollapsedmemory(true);
    } else if (!getButtonCollapsedMemory()) {
      setIsMenuCollapsed(false);
      setMenuCollapsedmemory(false);
    }
  }, [width]);

  const handleMenuToggle = () => {
    const newButtonCollapsed = !isButtonCollapsed;
    const newMenuCollapsed = !isMenuCollapsed;
    setIsButtonCollapsed(newButtonCollapsed);
    setButtonCollapsedmemory(newButtonCollapsed);
    setIsMenuCollapsed(newMenuCollapsed);
    setMenuCollapsedmemory(newMenuCollapsed);
  };

  useEffect(() => {
    const db = getDatabase();
    const usersRef = ref(db, "users");
    setIsLoading(true);

    const unsubscribe = onValue(
      usersRef,
      (snapshot) => {
        const data = snapshot.val();
        if (data) {
          const lista: Aniversariante[] = Object.values(data)
            .filter((u: any) => u.aniversario && u.bloq !== true)
            .map((u: any) => ({
              displayName: u.displayName || "Usuário",
              aniversario: u.aniversario,
            }));

          setAniversariantes(lista);
        }
        setIsLoading(false);
      },
      (error) => {
        console.error("Firebase error:", error);
        setIsLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  // 3. Renderização do Calendário (Otimizada para Mobile)
  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="flex min-h-[300px] flex-col items-center justify-center text-zinc-300">
          <FaSpinner className="mb-4 animate-spin text-4xl text-pink-300" />
          <span className="text-lg">Carregando dados...</span>
        </div>
      );
    }

    if (aniversariantes.length === 0) {
      return (
        <div className="flex min-h-[300px] flex-col items-center justify-center text-zinc-300">
          <FaRegSadTear className="mb-4 text-4xl text-pink-300" />
          <span className="text-lg font-medium">
            Nenhum aniversário cadastrado.
          </span>
          <span className="text-sm">Ainda não há dados no sistema.</span>
        </div>
      );
    }

    // Lógica do Calendário (Dias Vazios e Dias do Mês)
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const startingDayIndex = (firstDayOfMonth.getDay() + 6) % 7;
    const daysInMonth = new Date(
      today.getFullYear(),
      today.getMonth() + 1,
      0
    ).getDate();

    const days = [];

    // Dias vazios no início
    for (let i = 0; i < startingDayIndex; i++) {
      days.push(
        // Altura menor (min-h-20 para mobile, min-h-24 para desktop)
        <div
          key={`empty-${i}`}
          className="min-h-20 border-b border-r border-white/10 bg-black/10 p-1 sm:min-h-24"
        ></div>
      );
    }

    // Dias do mês
    for (let day = 1; day <= daysInMonth; day++) {
      const aniversarios = aniversariantesPorDia.get(day) || [];
      const isToday = day === diaAtual;
      const count = aniversarios.length;

      // Classes de altura adaptativas
      const heightClasses = isMobileView
        ? "min-h-20 max-h-24"
        : "min-h-24 max-h-40 overflow-hidden";

      days.push(
        <div
          key={day}
          className={`${heightClasses} border-b border-white/10 p-1 transition-colors sm:p-2
            ${(day + startingDayIndex) % 7 !== 0 ? "border-r" : ""} 
            ${
              isToday
                ? "bg-pink-500/20 shadow-[0_18px_40px_rgba(244,114,182,0.18)]"
                : "bg-white/[0.04] hover:bg-white/[0.08]"
            }
          `}
        >
          {/* Número do Dia */}
          <div
            className={`text-right font-bold text-xs sm:text-sm ${
              isToday ? "text-white" : "text-zinc-300"
            }`}
          >
            {day}
          </div>

          {/* CONTEÚDO ADAPTATIVO */}
          {count > 0 && isMobileView ? (
            // Visual Mobile: Bolinha com Ícone (sem o número)
            <div className="flex justify-end mt-1">
              <div
                title={`${count} aniversariante(s) em ${day}`}
                className="flex h-5 w-5 items-center justify-center rounded-full bg-gradient-to-br from-pink-500 to-rose-500 text-white"
              >
                <FaBirthdayCake className="w-2 h-3" />
              </div>
            </div>
          ) : (
            // Visual Desktop: Nomes com quebra de linha e scroll
            <div className="mt-1 space-y-1 h-full overflow-y-auto custom-scrollbar">
              {aniversarios.map((a, i) => (
                <div
                  key={i}
                  className="rounded-md bg-gradient-to-r from-pink-500/80 to-rose-500/70 px-1 py-[2px] text-[10px] font-medium text-white sm:text-xs"
                  title={a.displayName}
                >
                  <FaBirthdayCake className="inline mr-1 w-3 h-3" />
                  <span className="break-words">{a.displayName}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      );
    }

    const daysOfWeek = ["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"];

    return (
      <div className="panel-soft rounded-[1.5rem] border border-white/10 p-2 sm:p-4">
        <div className="mb-2 grid grid-cols-7 border-b border-white/10 text-center font-bold text-zinc-300">
          {daysOfWeek.map((day) => (
            <div key={day} className="py-1 text-xs sm:text-sm">
              {day}
            </div>
          ))}
        </div>

        <div className="-ml-px -mt-px grid grid-cols-7 border-l border-t border-white/10">
          {days}
        </div>

        {aniversariantesDoMesOrdenados.length === 0 && (
          <div className="flex flex-col items-center justify-center p-8 text-zinc-300">
            <FaRegSadTear className="mb-4 text-4xl text-pink-300" />
            <span className="text-lg font-medium">
              Nenhum aniversariante em{" "}
              {nomeMesAtual.charAt(0).toUpperCase() + nomeMesAtual.slice(1)}
            </span>
            <span className="text-sm">
              Os cards aparecerão automaticamente no mês correto.
            </span>
          </div>
        )}
      </div>
    );
  };

  // Lista de Detalhes para Mobile (sem alteração significativa)
  const renderMobileDetailsList = () => {
    if (!isMobileView || aniversariantesDoMesOrdenados.length === 0) {
      return null;
    }

    return (
      <div className="panel-soft mt-6 rounded-[1.5rem] p-4 sm:p-6">
        <h3 className="mb-4 border-b border-white/10 pb-2 text-lg font-semibold text-white">
          Lista de Aniversariantes do Mês
        </h3>
        <ul className="space-y-2">
          {aniversariantesDoMesOrdenados.map((a, index) => {
            const [, , dayStr] = a.aniversario.split("-");
            const isToday = Number(dayStr) === diaAtual;

            return (
              <li
                key={index}
                className={`flex items-center justify-between rounded-2xl p-3 transition-colors ${
                  isToday ? "bg-pink-500/20" : "bg-white/[0.05]"
                }`}
              >
                <div
                  className={`font-medium ${
                    isToday ? "text-white" : "text-zinc-200"
                  }`}
                >
                  <FaBirthdayCake className="inline mr-2 text-pink-500" />
                  {a.displayName}
                </div>
                <span
                  className={`font-bold w-10 text-center ${
                    isToday
                      ? "text-white border-b-2 border-white"
                      : "border-b-2 border-pink-400 text-pink-300"
                  }`}
                >
                  {dayStr}
                </span>
              </li>
            );
          })}
        </ul>
      </div>
    );
  };

  return (
    <>
      <AlertSuccess
        isOpen={successAlert.open}
        message={successAlert.message}
        onClose={() => setSuccessAlert({ open: false, message: "" })}
      />
      <AppShell
        isMenuCollapsed={isMenuCollapsed}
        onToggleMenu={handleMenuToggle}
      >
        <section className="panel-glass rounded-[2rem] p-4 text-white sm:p-8">
          <div className="mb-6 flex items-center justify-between gap-3">
            <h2 className="text-xl font-semibold sm:text-2xl">
              Aniversariantes do mês
            </h2>
            <span className="rounded-full border border-pink-300/20 bg-pink-500/10 px-3 py-1 text-xs font-medium text-pink-100">
              {nomeMesAtual.charAt(0).toUpperCase() + nomeMesAtual.slice(1)}
            </span>
          </div>
          {renderContent()}
          {renderMobileDetailsList()}
        </section>

        <div className="w-full py-2 text-center">
          <span className="text-xs text-zinc-500 sm:text-sm">
            Developed by Samuel Stork
          </span>
          <span className="text-xs text-zinc-600 sm:text-sm">
            {getVersionApp()}
          </span>
        </div>
      </AppShell>
    </>
  );
};

export default Aniversario;
